import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    createValidationResult,
    ensure,
    FormDialog,
    FormFields,
    FormTextField,
    FormValidationSummary,
    ShimmeredDetailsList,
    Spinner,
    Stack,
    useNotifications,
    usePerformMultipleActions
} from "sprint-common-web";

import { useFileRecordApi } from "../../../../../common/api/master-api/file-records/useFileRecordApi";
import { FileRecordStatus } from "../../../../../common/types/FileRecordStatus";
import { canFileRecordBeChangedToWaitingForClientResponse } from "../../../common/utils/validateFileRecordStatus";
import { CourseOfActionFormFields } from "../../../forms/status/status-details/course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "../../../forms/status/status-details/course-of-action/useCourseOfActionFormFieldsValues";
import { FileRecordStatusDetailsFormInputs } from "../../../forms/status/status-details/FileRecordStatusDetailsForm";
import { FileRecordListingContext } from "../../context/FileRecordListingContext";
import { FileRecordListingItem } from "../../types/FileRecordListingItem";
import { createUpdateFileRecordStatusColumns } from "../update-multiple-status/utils/createUpdateFileRecordStatusColumns";
import { validateDiscardedFileRecords } from "../update-multiple-status/utils/validateDiscardedFileRecords";

interface ChangeMultipleFileRecordsToWaitingForResponseDialogProps {
    onClose(): void;
}

export const ChangeMultipleFileRecordsToWaitingForResponseDialog: React.FC<ChangeMultipleFileRecordsToWaitingForResponseDialogProps> = ({
    onClose
}: ChangeMultipleFileRecordsToWaitingForResponseDialogProps) => {
    const { success, error } = useNotifications();
    const { changeFileRecordToWaitingForClientResponse } = useFileRecordApi();

    const { control, errors, handleSubmit, formState, reset } = useForm();

    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();

    const { updateFileRecordStatus, selectedItems } = useContext(FileRecordListingContext);

    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<FileRecordListingItem>({
        isAvailable: (item) => canFileRecordBeChangedToWaitingForClientResponse(item.status),
        selectedItems
    });

    const formHasErrors = createValidationResult([
        validateDiscardedFileRecords(items, selectedItems.length, FileRecordStatus.WaitingForClientResponse)
    ]);

    const areValidFileRecordsEmpty = !items.length;
    const columns = createUpdateFileRecordStatusColumns();

    const handleConfirm = async (inputs: FileRecordStatusDetailsFormInputs): Promise<void> => {
        const courseOfAction = courseOfActions.find((action) => action.id === inputs.courseOfActionId);
        const fixedBilling = inputs.fixedBillingId
            ? fixedBillings.find((billing) => billing.id === inputs.fixedBillingId)
            : undefined;

        ensure(courseOfAction);

        const results = await performAllItems(async (item) => {
            await changeFileRecordToWaitingForClientResponse({
                fileRecordId: item.id,
                courseOfActionId: inputs.courseOfActionId,
                fixedBillingId: inputs.fixedBillingId,
                details: inputs.details
            });

            updateFileRecordStatus(item.id, FileRecordStatus.WaitingForClientResponse, {
                courseOfAction: courseOfAction.name,
                fixedBilling: fixedBilling?.name,
                details: inputs.details
            });
        });

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success("Selected file records changed to Waiting for client response");
        } else {
            error("Changing to Waiting for client response failed");
        }
    };

    const handleClose = () => {
        onClose();
        reset();
    };

    if (areCourseOfActionsLoading || areFixedBillingsLoading) {
        return (
            <Stack
                styles={{
                    root: { padding: 10 }
                }}
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <FormDialog
            title="Change file records status to Waiting for client response"
            formState={formState}
            text="Are you sure you want to change given file records to Waiting for client response?"
            isBlocking={isActionInProgress}
            showCloseButton={false}
            submit={{
                onClick: handleSubmit(handleConfirm),
                disabled:
                    areValidFileRecordsEmpty ||
                    isActionInProgress ||
                    areCourseOfActionsLoading ||
                    areFixedBillingsLoading
            }}
            cancel={{ onClick: handleClose }}
            width="65%"
            contentHeight={500}
        >
            {!areValidFileRecordsEmpty && (
                <FormFields>
                    <CourseOfActionFormFields
                        control={control}
                        errors={errors}
                        courseOfActions={courseOfActions}
                        fixedBillings={fixedBillings}
                    />
                    <FormTextField
                        control={control}
                        label="Status details"
                        name="details"
                        multiline={true}
                        errors={errors}
                    />
                </FormFields>
            )}
            <FormValidationSummary validationResult={formHasErrors} />
            {items.length > 0 && <ShimmeredDetailsList columns={columns} items={items} layoutMode="justified" />}
        </FormDialog>
    );
};
